import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

// Layouts
import AdminLayout from '../../layout/AdminLayout';
import MerchantLayout from '../../layout/MerchantLayout';
import ProtectedRoute from '../../routes/ProtectedRoute';
import PublicRoute from '../../routes/PublicRoute';

// Pages
import Login from '../../pages/auth/Login';
import Register from '../../pages/auth/Register';
import VerifyEmail from '../../pages/auth/VerifyEmail';
import ForgotPassword from '../../pages/auth/ForgotPassword';
import ResetPassword from '../../pages/auth/ResetPassword';
import Dashboard from '../../pages/merchant/Dashboard';
import AllMembers from '../../pages/merchant/members/AllMembers';
import NewMember from '../../pages/merchant/members/NewMember';
import EditMember from '../../pages/merchant/members/EditMember';
import Settings from '../../pages/merchant/Settings';
import MemberDetails from '../../pages/merchant/members/MemberDetails';
import MemberTransactions from '../../pages/merchant/members/MemberTransactions';
import AdminDashboard from '../../pages/admin/AdminDashboard';
import AdminMerchants from '../../pages/admin/AdminMerchants';
import AdminUsers from '../../pages/admin/AdminUsers';
import Onboarding from '../../pages/auth/Onboarding';
import OnboardingRoute from '../../routes/OnboardingRoute';
import Branches from '../../pages/merchant/branches/AllBranches';
import NewBranch from '../../pages/merchant/branches/NewBranch';
import BranchDetails from '../../pages/merchant/branches/BranchDetails';
import EditBranch from '../../pages/merchant/branches/EditBranch';
import AdminFeatures from '../../pages/admin/AdminFeatures';
import AdminModules from '../../pages/admin/AdminModules';
import AdminLimits from '../../pages/admin/AdminLimits';
import AdminMerchantDetails from '../../pages/admin/AdminMerchantDetails';
import AdminUserDetails from '../../pages/admin/AdminUserDetails';
import OnboardingSuccess from '../onboarding/OnboardingSuccess';
import PendingApprovalRoute from '../../routes/PendingApprovalRoute';
import { useMerchant } from '../../context/MerchantContext';
import AdminPlans from '../../pages/admin/plans/AdminPlans';
import AdminPlanEdit from '../../pages/admin/plans/AdminPlanEdit';
import AdminCreatePlan from '../../pages/admin/plans/AdminCreatePlan';
import AdminPlanDetails from '../../pages/admin/plans/AdminPlanDetails';
import AdminDiscounts from '../../pages/admin/discount/AdminDiscount';
import AdminCreateDiscount from '../../pages/admin/discount/AdminCreateDiscount';
import AdminDiscountDetails from '../../pages/admin/discount/AdminDiscountDetails';
import AdminEditDiscount from '../../pages/admin/discount/AdminEditDiscount';
import NotFound from '../../pages/NotFound';
import AllEvents from '../../pages/merchant/events/AllEvents';
import NewEvent from '../../pages/merchant/events/NewEvent';
import EventDetails from '../../pages/merchant/events/EventDetails';
import EventAttendance from '../../pages/merchant/events/EventAttendance';
import Attendance from '../../pages/merchant/attendance/Attendance';
import EventRegistrations from '../../pages/merchant/events/EventRegistrations';
import GuestManagement from '../../pages/merchant/events/GuestManagement';
import EventCheckIn from '../../pages/public/EventCheckIn';
import PublicRegistration from '../../pages/public/PublicRegistration';
import EventRegistrationPage from '../../pages/public/EventRegistrationPage';
import AllDepartments from '../../pages/merchant/departments/AllDepartments';
import DepartmentForm from '../../pages/merchant/departments/DepartmentForm';
import DepartmentDetails from '../../pages/merchant/departments/DepartmentDetails';
import DepartmentAdminDashboard from '../../pages/merchant/departments/DepartmentAdminDashboard';
import { useAuth } from '../../context/AuthContext';
import SMSDashboard from '../../pages/merchant/messaging/SMSDashboard';
import SendSMS from '../../pages/merchant/messaging/SendSMS';
import SMSTemplates from '../../pages/merchant/messaging/SMSTemplates';
import SMSHistory from '../../pages/merchant/messaging/SMSHistory';
import SMSCredits from '../../pages/merchant/messaging/SMSCredits';
import SMSPackages from '../../pages/admin/sms/AdminSMSPackages';
import SmsBranchGate from '../../components/messaging/SmsBranchGate';
import Birthdays from '../../pages/merchant/members/Birthdays';
import BranchMembers from '../../pages/merchant/branches/BranchMembers';
import BirthdaySettings from '../../pages/merchant/members/BirthdaySettings';
import PartnershipProgrammes from '../../pages/merchant/members/partnership/PartnershipProgrammes';
import NewPartnership from '../../pages/merchant/members/partnership/NewPartnership';
import PartnershipDetails from '../../pages/merchant/members/partnership/PartnershipDetails';
import PublicPartnershipRegistration from '../../pages/public/PublicPartnershipRegistration';
import PublicPartnershipPayment from '../../pages/public/PublicPartnershipPayment';
import PublicProjectRegistration from '../../pages/public/PublicProjectRegistration';
import PublicProjectPayment from '../../pages/public/PublicProjectPayment';
import PublicEventGiving from '../../pages/public/PublicEventGiving';
import PartnershipPaymentStatus from '../../pages/public/PartnershipPaymentStatus';
import AdminLogs from '../../pages/admin/logs/AdminLogs';
import SMSSettings from '../../pages/merchant/messaging/SMSSettings';
import AdminSenderIds from '../../pages/admin/sms/AdminSenderIds';
import SelectChurch from '../../pages/admin/SelectChurch';
import AuthRedirect from '../../pages/auth/AuthRedirect';
import AdminRolesPage from '../../pages/admin/AdminRolesPage';
import AdminAnnouncements from '../../pages/admin/AdminAnnouncements';
import AdminComms from '../../pages/admin/AdminComms';
import Wallet from '../../pages/merchant/finance/Wallet';
import ActivityLogs from '../../pages/merchant/ActivityLogs';
import WithdrawalManagement from '../../pages/admin/WithdrawalManagement';

import FinanceKYCManagement from '../../pages/admin/FinanceKYCManagement';
// import SermonManagement from '../../pages/merchant/sermons/SermonManagement';
import AudioSermons from '../../pages/merchant/sermons/AudioSermons';
import VideoSermons from '../../pages/merchant/sermons/VideoSermons';
import Preachers from '../../pages/merchant/sermons/Preachers';
import AudioDistribution from '../../pages/merchant/sermons/AudioDistribution';
import TransactionManagement from '../../pages/admin/TransactionManagement';
import AdminFinanceOverview from '../../pages/admin/FinanceOverview';
import AllTransactions from '../../pages/merchant/finance/AllTransactions';

// Calendar
import CalendarPage from '../../pages/merchant/calendar/CalendarPage';

// Social Media
import SocialDashboard from '../../pages/merchant/social-media/SocialDashboard';
import SocialAccounts from '../../pages/merchant/social-media/SocialAccounts';
import SocialCalendar from '../../pages/merchant/social-media/SocialCalendar';
import SocialPosts from '../../pages/merchant/social-media/SocialPosts';
import CreateSocialPost from '../../pages/merchant/social-media/CreatePost';
import SocialPostDetail from '../../pages/merchant/social-media/PostDetail';
import SocialTemplates from '../../pages/merchant/social-media/SocialTemplates';
import SocialAnalytics from '../../pages/merchant/social-media/SocialAnalytics';

// Documentation
import Documentation from '../../pages/merchant/Documentation';

// Support
import Support from '../../pages/merchant/support/Support';
import SupportTicketDetail from '../../pages/merchant/support/SupportTicketDetail';
import AdminSupport from '../../pages/admin/support/AdminSupport';
import AdminSupportTicketDetail from '../../pages/admin/support/AdminSupportTicketDetail';

// Giving Module
import Projects from '../../pages/merchant/giving/projects/Projects';
import ProjectDetails from '../../pages/merchant/giving/projects/ProjectDetails';
import NewProject from '../../pages/merchant/giving/projects/NewProject';
import Tithes from '../../pages/merchant/giving/Tithes';
import Offerings from '../../pages/merchant/giving/Offerings';
import GivingBranchGate from '../../components/giving/GivingBranchGate';

// Add this wrapper component
const RegisterGuard = ({ children }: { children: React.ReactNode }) => {
  const { isMainDomain, loading } = useMerchant();


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Only allow register on main domain
  if (!isMainDomain) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};


const AnimatedRoutes = () => {
  const { user } = useAuth();
  const { merchant, isMainDomain } = useMerchant();

  // Update document title based on subdomain
  useEffect(() => {
    if (merchant) {
      // On church subdomain: "Faith Church - The Church HQ"
      document.title = `${merchant.name} - The Church HQ`;
    } else if (isMainDomain) {
      // On main domain: "The Church HQ - Church Management Platform"
      document.title = 'The Church HQ - Church Management Platform';
    }
  }, [merchant, isMainDomain]);

  // Update favicon based on merchant logo (optional)
  useEffect(() => {
    if (merchant?.branding?.logo) {
      const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
      if (link) {
        link.href = merchant.branding.logo;
      }
    }
  }, [merchant]);

  return (
    <Routes>
        {/* Landing Page - Main Domain */}
        {isMainDomain && <Route path="/login" element={<Login />} />}

        {/* Public Routes */}
        <Route element={<PublicRoute />}>
          <Route path="/login" element={<Login />} />
          <Route path="/auth-redirect" element={<AuthRedirect />} />
          <Route path="/register" element={
            <RegisterGuard>
              <Register />
            </RegisterGuard>
          } />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* Recurring event direct URL */}
          <Route path="/attend/:eventId" element={<EventCheckIn />} />
          {/* QR code URL patterns */}
          <Route path="/events/attend/:qrData" element={<EventCheckIn />} />
          <Route path="/events/attend/:eventId" element={<EventCheckIn />} />
          <Route path="/events/register/:eventId" element={<EventRegistrationPage />} />
          <Route path="/register/:merchantId" element={<PublicRegistration />} />
          <Route path="/partnership/register/:merchantId/:programmeId" element={<PublicPartnershipRegistration />} />
          <Route path="/partnership/payment/:merchantId/:programmeId" element={<PublicPartnershipPayment />} />
          <Route path="/projects/register/:merchantId/:id" element={<PublicProjectRegistration />} />
          <Route path="/projects/payment/:merchantId/:id" element={<PublicProjectPayment />} />
          <Route path="/partnership/payment/:merchantId/:programmeId/status" element={<PartnershipPaymentStatus />} />
          <Route path="/give/:uniqueId" element={<PublicEventGiving />} />
          <Route path="/give/:uniqueId/status" element={<PublicEventGiving />} />
        </Route>

        {/* Onboarding Route - Requires auth but not completed onboarding */}
        <Route element={<OnboardingRoute />}>
          <Route path="/onboarding" element={<Onboarding />} />
        </Route>

        {/* Pending Approval Route - NEW! */}
        <Route element={<PendingApprovalRoute />}>
          <Route path="/onboarding/success" element={<OnboardingSuccess />} />
        </Route>

        {/* Protected Routes - Church Dashboard */}
        <Route element={<ProtectedRoute />}>
          <Route element={<MerchantLayout />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/branches" element={<Branches />} />
            <Route path="/branches/new" element={<NewBranch />} />
            <Route path="/branches/:id" element={<BranchDetails />} />
            <Route path="/branches/:id/edit" element={<EditBranch />} />
            <Route path="/branches/:id/members" element={<BranchMembers />} />

            <Route path="/members/all" element={<AllMembers />} />
            <Route path="/members/new" element={<NewMember />} />
            <Route path="/members/:id" element={<MemberDetails />} />
            <Route path="/members/:id/edit" element={<EditMember />} />
            <Route path="/members/:id/transactions" element={<MemberTransactions />} />
            <Route path="/members/birthdays" element={<Birthdays />} />
            <Route path="/members/birthdays/settings" element={<BirthdaySettings />} />
            <Route path="/members/partnership" element={<PartnershipProgrammes />} />
            <Route path="/members/partnership/new" element={<NewPartnership />} />
            <Route path="/members/partnership/:id/edit" element={<NewPartnership />} />
            <Route path="/members/partnership/:id" element={<PartnershipDetails />} />

            <Route path="/services" element={<AllEvents mode="services" />} />
            <Route path="/events" element={<AllEvents mode="events" />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/services/new" element={<NewEvent fixedType="service" />} />
            <Route path="/services/:id" element={<EventDetails />} />
            <Route path="/services/:id/edit" element={<NewEvent fixedType="service" />} />
            <Route path="/attendance" element={<Attendance />} />
            <Route path="/events/new" element={<NewEvent fixedType="event" />} />
            <Route path="/events/:id" element={<EventDetails />} />
            <Route path="/events/:id/edit" element={<NewEvent fixedType="event" />} />
            <Route path="/events/:id/attendance" element={<EventAttendance />} />
            <Route path="/events/:id/registrations" element={<EventRegistrations />} />
            <Route path="/events/guests" element={<GuestManagement />} />


            <Route
              path="/departments"
              element={
                user?.role?.slug === 'dept_admin'
                  ? <DepartmentAdminDashboard />
                  : <AllDepartments />
              }
            />
            <Route path="/departments/new" element={<DepartmentForm />} />
            <Route path="/departments/:id" element={<DepartmentDetails />} />
            <Route path="/departments/:id/edit" element={<DepartmentForm />} />

            {/* SMS Routes — all require branch selection (credits are branch-scoped) */}
            <Route path="/messaging/analytics" element={<SmsBranchGate><SMSDashboard /></SmsBranchGate>} />
            <Route path="/messaging/send" element={<SmsBranchGate><SendSMS /></SmsBranchGate>} />
            <Route path="/messaging/templates" element={<SmsBranchGate><SMSTemplates /></SmsBranchGate>} />
            <Route path="/messaging/history" element={<SmsBranchGate><SMSHistory /></SmsBranchGate>} />
            <Route path="/messaging/credits" element={<SmsBranchGate><SMSCredits /></SmsBranchGate>} />
            <Route path="/messaging/sender-id" element={<SmsBranchGate><SMSSettings /></SmsBranchGate>} />

            {/* Finance Routes */}
            <Route path="/finance/wallet" element={<Wallet />} />
            <Route path="/finance/transactions" element={<AllTransactions />} />

            {/* Giving Module Routes */}
            <Route path="/giving/tithes" element={<GivingBranchGate><Tithes /></GivingBranchGate>} />
            <Route path="/giving/offerings" element={<GivingBranchGate><Offerings /></GivingBranchGate>} />
            <Route path="/giving/projects" element={<GivingBranchGate><Projects /></GivingBranchGate>} />
            <Route path="/giving/projects/new" element={<GivingBranchGate><NewProject /></GivingBranchGate>} />
            <Route path="/giving/projects/:id" element={<GivingBranchGate><ProjectDetails /></GivingBranchGate>} />
            <Route path="/giving/projects/:id/edit" element={<GivingBranchGate><NewProject /></GivingBranchGate>} />

            {/* Sermon Routes */}
            {/* <Route path="/sermons" element={<SermonManagement />} /> */}
            <Route path="/sermons/audio" element={<AudioSermons />} />
            <Route path="/sermons/video" element={<VideoSermons />} />
            <Route path="/sermons/preachers" element={<Preachers />} />
            <Route path="/sermons/distribution" element={<AudioDistribution />} />

            {/* Social Media Routes */}
            <Route path="/social-media/dashboard" element={<SocialDashboard />} />
            <Route path="/social-media/accounts" element={<SocialAccounts />} />
            <Route path="/social-media/calendar" element={<SocialCalendar />} />
            <Route path="/social-media/posts" element={<SocialPosts />} />
            <Route path="/social-media/create" element={<CreateSocialPost />} />
            <Route path="/social-media/posts/:id" element={<SocialPostDetail />} />
            <Route path="/social-media/posts/:id/edit" element={<CreateSocialPost />} />
            <Route path="/social-media/templates" element={<SocialTemplates />} />
            <Route path="/social-media/analytics" element={<SocialAnalytics />} />

            {/* Documentation Route */}
            <Route path="/documentation" element={<Documentation />} />

            <Route path="/activity-logs" element={<ActivityLogs />} />
            <Route path="/support" element={<Support />} />
            <Route path="/support/:id" element={<SupportTicketDetail />} />
            <Route path="/settings" element={<Settings />} />
          </Route>
        </Route>

        {/* Protected Routes - Super Admin Dashboard */}
        <Route element={<ProtectedRoute requiredRole="super_admin" />}>
          {/* Church Selection Route - Before accessing merchant features */}
          <Route path="/select-church" element={<SelectChurch />} />

          <Route element={<AdminLayout />}>
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/churches" element={<AdminMerchants />} />
            <Route path="/admin/churches/:id" element={<AdminMerchantDetails />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/users/:id" element={<AdminUserDetails />} />
            <Route path="/admin/features" element={<AdminFeatures />} />
            <Route path="/admin/modules" element={<AdminModules />} />
            <Route path="/admin/limits" element={<AdminLimits />} />

            {/* Plans */}
            <Route path="/admin/plans" element={<AdminPlans />} />
            <Route path="/admin/plans/new" element={<AdminCreatePlan />} />
            <Route path="/admin/plans/:id" element={<AdminPlanDetails />} />
            <Route path="/admin/plans/:id/edit" element={<AdminPlanEdit />} />

            {/* Discounts */}
            <Route path="/admin/discounts" element={<AdminDiscounts />} />
            <Route path="/admin/discounts/new" element={<AdminCreateDiscount />} />
            <Route path="/admin/discounts/:id" element={<AdminDiscountDetails />} />
            <Route path="/admin/discounts/:id/edit" element={<AdminEditDiscount />} />

            {/* ✅ NEW: Messaging Routes */}
            <Route path="/admin/sender-ids" element={<AdminSenderIds />} />
            <Route path="/admin/sms-packages" element={<SMSPackages />} />

            {/* Roles & Permissions */}
            <Route path="/admin/roles" element={<AdminRolesPage />} />

            {/* Finance Management */}
            <Route path="/admin/finance-overview" element={<AdminFinanceOverview />} />
            <Route path="/admin/finance-kyc" element={<FinanceKYCManagement />} />
            <Route path="/admin/transactions" element={<TransactionManagement />} />
            <Route path="/admin/withdrawals" element={<WithdrawalManagement />} />

            {/* Announcements */}
            <Route path="/admin/announcements" element={<AdminAnnouncements />} />
            <Route path="/admin/comms" element={<AdminComms />} />
            <Route path="/admin/logs" element={<AdminLogs />} />
            <Route path="/admin/support" element={<AdminSupport />} />
            <Route path="/admin/support/:id" element={<AdminSupportTicketDetail />} />
          </Route>
        </Route>

        {/* Default */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
  );
};

export default AnimatedRoutes;